import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PrismLoading } from '@prism-ui/react';
import { toast } from '../../../Toaster/toast';

const ApprovalTypesTab = ({ currentUser, onConfigChange }) => {
  const [chapters, setChapters] = useState([]);
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const isAdmin = currentUser?.Role?.permission_level >= 100;
  const userChapterId = currentUser?.chapter_id;
  const canEditChapter = (chapterId) => isAdmin || chapterId === userChapterId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [chaptersRes, configsRes] = await Promise.all([
          axios.get('/api/chapters'),
          axios.get('/api/manager_approval_type_config'),
        ]);
        setChapters(chaptersRes.data || []);
        const configMap = {};
        (configsRes.data || []).forEach(c => { configMap[c.chapter_id] = c; });
        setConfigs(configMap);
      } catch (err) {
        console.error('Error fetching type configs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async (chapterId) => {
    setSaving(prev => ({ ...prev, [chapterId]: true }));
    const config = configs[chapterId] || { mentee_required: true, mentor_required: false, tcm_required: false };
    try {
      const res = await axios.put(`/api/manager_approval_type_config/${chapterId}`, {
        mentee_required: config.mentee_required,
        mentor_required: config.mentor_required,
        tcm_required: config.tcm_required,
      });
      setConfigs(prev => ({ ...prev, [chapterId]: res.data }));
      toast.success('Configuration saved');
      if (onConfigChange) onConfigChange();
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(prev => ({ ...prev, [chapterId]: false }));
    }
  };

  const updateConfig = (chapterId, field, value) => {
    setConfigs(prev => ({
      ...prev,
      [chapterId]: { ...(prev[chapterId] || { chapter_id: chapterId, mentee_required: true, mentor_required: false, tcm_required: false }), [field]: value },
    }));
  };

  if (loading) return <PrismLoading as="spinner" container="inline" />;

  return (
    <div>
      <p style={{ color: 'var(--color-text-faded)', marginBottom: '16px', fontSize: '0.9em' }}>
        Enabling a type means managers will receive approval requests and reminders for that applicant type.
        Disabling removes them from the manager's view.
      </p>

      <div className="manager-approval-card-grid manager-approval-card-grid--compact">
      {chapters.map(chapter => {
        const config = configs[chapter.id] || { mentee_required: true, mentor_required: false, tcm_required: false };
        const isSaving = saving[chapter.id];
        const canEdit = canEditChapter(chapter.id);

        return (
          <div key={chapter.id} style={{ border: '1px solid var(--color-border)', borderRadius: '8px', padding: '16px', marginBottom: '16px', opacity: canEdit ? 1 : 0.7 }}>
            <h4 style={{ margin: '0 0 12px 0' }}>{chapter.location}</h4>

            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={config.mentee_required !== false}
                  onChange={(e) => updateConfig(chapter.id, 'mentee_required', e.target.checked)}
                  disabled={!canEdit}
                />
                Mentee Required <span style={{ color: 'var(--color-text-faded)', fontSize: '0.85em' }}>(default)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={config.mentor_required || false}
                  onChange={(e) => updateConfig(chapter.id, 'mentor_required', e.target.checked)}
                  disabled={!canEdit}
                />
                Mentor Required
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={config.tcm_required || false}
                  onChange={(e) => updateConfig(chapter.id, 'tcm_required', e.target.checked)}
                  disabled={!canEdit}
                />
                TCM/Committee Required
              </label>
              <button
                onClick={() => handleSave(chapter.id)}
                disabled={isSaving || !canEdit}
                style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: '4px', border: 'none', backgroundColor: 'var(--color-primary)', color: '#fff', cursor: canEdit ? 'pointer' : 'not-allowed' }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default ApprovalTypesTab;
